import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AnnouncementService } from '@edtech-ui/core/services/announcement-management/announcement.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, Subscription, switchMap, tap, throwError } from 'rxjs';
import { ReceivedStudentAnnouncement, SentStudentAnnouncement, StudentStudyMaterialResponse } from '@edtech-ui/core/models/material-upload/get-student-all-announcement';
import { IstDatePipe } from '@edtech-ui/core/models/Classroom/ist-date.pipe';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { StudentService } from '@edtech-ui/core/services/student-management/student.service';
import { NotificationsData } from '@edtech-ui/core/models/student-recent-updates/student-recent-quiz';
import { NotificationTypeEnum } from '@edtech-ui/constants/notification.enum';
import { StudentMarkContentVisitedPayload } from '@edtech-ui/core/models/student-recent-updates/student-updated-marked';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { CompleteSyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';

@Component({
	selector: 'app-student-classroom-overview',
	standalone: true,
	imports: [CommonModule, MatCardModule, MatIconModule, MatTooltipModule, IstDatePipe, MatMenuModule, MatProgressSpinnerModule],
	templateUrl: './student-classroom-overview.component.html',
	styleUrls: ['./student-classroom-overview.component.scss']
})
export class StudentClassroomOverviewComponent implements OnInit, OnDestroy {
	classroomId!: number;
	subscription = new Subscription();
	studentAnnouncementReceived: ReceivedStudentAnnouncement[] = [];
	studentAnnouncementSent: SentStudentAnnouncement[] = [];
	studentRecentUpdate: NotificationsData[] = [];
	totalRecentUpdateCount: number = 0;
	notificationTypeEnum = NotificationTypeEnum;
	isLoadingAnnouncements = false;
	isLoadingRecentUpdates = false;
	totalCount: number = 0;

	constructor(
		private route: ActivatedRoute,
		private _announcementService: AnnouncementService,
		private _toastr: ToastrService,
		private _studentService: StudentService,
		private router: Router,
		private _courseService: CourseManagementService
	) { }

	get isLoading(): boolean {
		return this.isLoadingAnnouncements || this.isLoadingRecentUpdates;
	}

	ngOnInit(): void {
		this.classroomId = Number(this.route.parent?.snapshot.paramMap.get('id'));
		this.getAllStudentAnnouncement();
		this.getStudentRecentsUpdates();
	}

	getAllStudentAnnouncement() {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is missing');
			return;
		}
		this.isLoadingAnnouncements = true;
		this.subscription.add(
			this._announcementService.getAllStudentAnnouncement(Number(this.classroomId)).subscribe({
				next: (response: StudentStudyMaterialResponse) => {
					this.studentAnnouncementReceived = response.Data.received_announcements;
					this.studentAnnouncementSent = response.Data.sent_announcements;
					this.totalCount = response.Data.sent_count + response.Data.received_count;
					this.isLoadingAnnouncements = false;
				},
				error: () => {
					this._toastr.error('Failed to load announcements');
					this.isLoadingAnnouncements = false;
				}
			})
		);
	}

	getStudentRecentsUpdates(): void {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is missing');
			return;
		}
		this.isLoadingRecentUpdates = true;
		this.subscription.add(
			this._studentService.getStudentsRecentUpdates(Number(this.classroomId)).pipe(
				tap(response => {
					if (response.Success) {
						this.studentRecentUpdate = response.Data.notifications || [];
						this.totalRecentUpdateCount = response.Data.total_count;
					}
					this.isLoadingRecentUpdates = false;
				}),
				catchError(err => {
					this.isLoadingRecentUpdates = false;
					this._toastr.error('Failed to load recent updates');
					throw err;
				}
				)).subscribe());
	}

	putStudentMarkedAsVisitCourse(studentUpdate: NotificationsData): void {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is missing');
			return;
		}

		const payload: StudentMarkContentVisitedPayload = {
			content_id: studentUpdate.id,
			content_type: NotificationTypeEnum.Course,
			classroom_id: this.classroomId
		};

		this.subscription.add(
			this._studentService.postStudentRecentMarkedVisited(payload).pipe(
				switchMap(response => {
					if (response.Success) {
						return this._courseService.getCoursesByCourseId(studentUpdate.id);
					} else {
						this._toastr.error('Failed to mark course as visited');
						return throwError(() => new Error('Mark visited failed'));
					}
				}),
				tap((courseResponse: CompleteSyllabusResponseAI) => {
					const course = courseResponse.Data;
					if (!course) {
						this._toastr.error('Course not found');
						return;
					}

					const route = course.is_ai
						? ['student-dashboard/course-view', this.classroomId, studentUpdate.id]
						: ['student-dashboard/manual-course-view', this.classroomId, studentUpdate.id];

					this.router.navigate(route);
				}),
				catchError(err => {
					this._toastr.error('Failed to load course details');
					throw err;
				})
			).subscribe()
		);
	}

	putStudentMarkedAsVisitQuiz(studentUpdate: NotificationsData): void {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is missing');
			return;
		}

		const payload: StudentMarkContentVisitedPayload = {
			content_id: studentUpdate.id,
			content_type: NotificationTypeEnum.Quiz,
			classroom_id: this.classroomId
		};

		this.subscription.add(
			this._studentService.postStudentRecentMarkedVisited(payload).pipe(
				tap(response => {
					if (response.Success) {
						this.router.navigate(['student-dashboard', 'classroom-detail-page', this.classroomId,'quiz']);
					}
				}),
				catchError(err => {
					this._toastr.error('Failed to load recent updates');
					throw err;
				}
				)).subscribe()
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}